import React, { useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { REPORT_TYPE } from 'constants/constantVariables';
import './MonitoringSetup.scss';

// The single source of truth for monitoring setup, used by BOTH the per-application drawer (mode="single")
// and the bulk table dialog (mode="bulk"). Same layout and model in both; only the report list source and
// the primary action wording differ. Per-report cadence + an explicit start/end window drive the schedule;
// sub-daily cadences (hourly / 6-hourly) are FMCSA-only.

const CADENCES = [
    { value: 'HOURLY', label: 'Hourly', step: ['hour', 1], runsPerMonth: 730 },
    { value: 'EVERY_6_HOURS', label: 'Every 6 hours', step: ['hour', 6], runsPerMonth: 730 / 6 },
    { value: 'DAILY', label: 'Daily', step: ['day', 1], runsPerMonth: 30 },
    { value: 'WEEKLY', label: 'Weekly', step: ['day', 7], runsPerMonth: 30 / 7 },
    { value: 'EVERY_2_WEEKS', label: 'Every 2 weeks', step: ['day', 14], runsPerMonth: 30 / 14 },
    { value: 'EVERY_1_MONTH', label: 'Monthly', step: ['month', 1], runsPerMonth: 1 },
    { value: 'EVERY_2_MONTHS', label: 'Every 2 months', step: ['month', 2], runsPerMonth: 1 / 2 },
    { value: 'EVERY_3_MONTHS', label: 'Every 3 months', step: ['month', 3], runsPerMonth: 1 / 3 },
    { value: 'EVERY_4_MONTHS', label: 'Every 4 months', step: ['month', 4], runsPerMonth: 1 / 4 },
    { value: 'EVERY_6_MONTHS', label: 'Every 6 months', step: ['month', 6], runsPerMonth: 1 / 6 },
    { value: 'EVERY_12_MONTHS', label: 'Yearly', step: ['month', 12], runsPerMonth: 1 / 12 },
];
const CAD = Object.fromEntries(CADENCES.map(c => [c.value, c]));
const DEFAULT_CADENCE = 'EVERY_1_MONTH';
const isSubDaily = (value) => value === 'HOURLY' || value === 'EVERY_6_HOURS';
const cadenceLabel = (value) => (CAD[value] || CAD[DEFAULT_CADENCE]).label;

// Exact run count in [start, end]: first run is one interval after the start, then every interval, counted
// while on/before the end. Returns 0 when the window is shorter than the cadence.
// Number of checks between the (already-snapped) first check and the window end, INCLUSIVE of the first
// check. Counting from the snapped first check — not the raw start — avoids over-counting when the actual
// schedule snaps to the next top-of-hour (e.g. an hourly schedule starting at 10:30 first runs at 12:00).
const runsInWindow = (cadence, firstCheck, end) => {
    if (!firstCheck || !end) return null;
    const f = dayjs(firstCheck);
    const e = dayjs(end);
    if (f.isAfter(e)) return 0;
    const step = (CAD[cadence] || CAD[DEFAULT_CADENCE]).step;
    return Math.floor(e.diff(f, step[0]) / step[1]) + 1;
};

// First actual check: one interval after the effective start, snapped up to the next top-of-hour (sweeps
// only run on the hour).
const firstCheckOf = (cadence, effectiveStart) => {
    if (!effectiveStart) return null;
    const step = (CAD[cadence] || CAD[DEFAULT_CADENCE]).step;
    const due = effectiveStart.add(step[1], step[0]);
    return due.isSame(due.startOf('hour')) ? due : due.startOf('hour').add(1, 'hour');
};

// A custom cadence dropdown so disabled (sub-daily, non-FMCSA) options can carry an explanatory tooltip —
// a native <select> can't. Closed by an invisible full-screen backdrop.
const CadenceSelect = ({ value, disabled, allowSubDaily, onChange }) => {
    const [open, setOpen] = useState(false);
    const [tip, setTip] = useState(null);
    return (
        <div className='monSetup__cadWrap'>
            <button
                type='button'
                className='monSetup__cad'
                disabled={disabled}
                onClick={() => setOpen(o => !o)}
            >
                {value ? cadenceLabel(value) : 'Cadence'}
                <span className='monSetup__cadCaret'>▾</span>
            </button>
            {open && (
                <>
                    <div className='monSetup__menuBackdrop' onClick={() => { setOpen(false); setTip(null); }} />
                    <div className='monSetup__menu' role='listbox'>
                        {CADENCES.map(c => {
                            const optDisabled = isSubDaily(c.value) && !allowSubDaily;
                            if (optDisabled) {
                                return (
                                    <div
                                        key={c.value}
                                        className='monSetup__opt is-disabled'
                                        role='option'
                                        aria-selected={false}
                                        aria-disabled='true'
                                        onMouseEnter={() => setTip(c.value)}
                                        onMouseLeave={() => setTip(t => (t === c.value ? null : t))}
                                    >
                                        {c.label}
                                        {tip === c.value &&
                                            <span className='monSetup__tip'>Sub-daily cadences are only available for FMCSA.</span>}
                                    </div>
                                );
                            }
                            return (
                                <button
                                    key={c.value}
                                    type='button'
                                    role='option'
                                    aria-selected={value === c.value}
                                    className='monSetup__opt'
                                    onClick={() => { onChange(c.value); setOpen(false); setTip(null); }}
                                >
                                    <span>{c.label}</span>
                                    {value === c.value && <span className='monSetup__optCheck'>✓</span>}
                                </button>
                            );
                        })}
                    </div>
                </>
            )}
        </div>
    );
};

const MonitoringSetup = ({
    mode = 'single',
    reports = [],
    balance = null,
    businessCount = 1,
    businesses = [],
    initialSelections = {},
    initialStart = null,
    initialEnd = null,
    initialOngoing = true,
    busy = false,
    onPrimary,
    onCancel,
}) => {
    const bulk = mode === 'bulk';
    const mult = bulk ? Math.max(businessCount, 1) : 1;
    const todayISO = dayjs().format('YYYY-MM-DD');

    // selections: reportKey -> cadence value (present = checked).
    const [sel, setSel] = useState(initialSelections);
    const [startISO, setStartISO] = useState(initialStart || todayISO);
    const [endISO, setEndISO] = useState(initialEnd || '');
    const [ongoing, setOngoing] = useState(initialOngoing);

    // Re-seed when the source data changes (e.g. drawer opens with a fresh config).
    useEffect(() => { setSel(initialSelections); }, [initialSelections]);
    useEffect(() => {
        setStartISO(initialStart || todayISO);
        setEndISO(initialEnd || '');
        setOngoing(initialOngoing);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialStart, initialEnd, initialOngoing]);

    const toggle = (report) => {
        if (!report.available) return;
        setSel(prev => {
            const next = { ...prev };
            if (next[report.key]) delete next[report.key];
            else next[report.key] = DEFAULT_CADENCE;
            return next;
        });
    };
    const setCadence = (report, value) => setSel(prev => ({ ...prev, [report.key]: value }));

    const endBeforeStart = !ongoing && endISO && dayjs(endISO).isBefore(dayjs(startISO));
    const bounded = !ongoing && !!endISO && !endBeforeStart;
    const effectiveStart = dayjs(startISO).isAfter(dayjs()) ? dayjs(startISO) : dayjs();
    const windowEnd = endISO ? dayjs(endISO).endOf('day') : null;
    // Distinguish "the window is already over" from "the window is just too short for the cadence" — the two
    // reasons a report won't run need different guidance (pick a future date vs. lengthen the window).
    const windowEnded = bounded && windowEnd && windowEnd.isBefore(dayjs());

    // Per-report cost lines + running totals.
    const { lines, wont, total, firstCycle, runningCount } = useMemo(() => {
        const out = [];
        const wontRun = [];
        let sum = 0;
        let cycle = 0;
        let running = 0;
        reports.forEach(r => {
            const cadence = sel[r.key];
            if (!cadence || !r.available) return;
            const credits = r.credits || 0;
            const first = firstCheckOf(cadence, effectiveStart);
            if (bounded) {
                const runs = runsInWindow(cadence, first, windowEnd);
                if (!runs) {
                    wontRun.push(r.label);
                    out.push({ key: r.key, label: r.label, cadence, zero: true });
                    return;
                }
                sum += credits * runs * mult;
                out.push({
                    key: r.key, label: r.label, cadence, credits,
                    creditsText: `${credits} credit${credits === 1 ? '' : 's'}/check${bulk ? ' per business' : ''}`,
                    sub: `First check ${first.format('MMM DD, YYYY [at] HH:mm')} · ${runs} check${runs === 1 ? '' : 's'}${bulk ? ` × ${mult} businesses` : ''}`,
                });
            } else {
                sum += credits * (CAD[cadence] || CAD[DEFAULT_CADENCE]).runsPerMonth * mult;
                out.push({
                    key: r.key, label: r.label, cadence, credits,
                    creditsText: `${credits} credit${credits === 1 ? '' : 's'}/check${bulk ? ' per business' : ''}`,
                    sub: `First check ${first.format('MMM DD, YYYY [at] HH:mm')}${bulk ? ` · × ${mult} businesses` : ''}`,
                });
            }
            cycle += credits * mult;
            running += 1;
        });
        return { lines: out, wont: wontRun, total: Math.round(sum), firstCycle: cycle, runningCount: running };
    }, [reports, sel, bounded, effectiveStart, windowEnd, mult, bulk]);

    // A null balance means the client is NOT on the credit system (no balance record). Monitoring works
    // exactly the same for them, but nothing credit-related is shown — no costs, no balance, no warnings.
    // A number (including 0) means a credit client, and the full cost/balance UI applies.
    const showCredits = balance != null;

    const reportsWord = `${runningCount} report${runningCount === 1 ? '' : 's'}`;
    const totalText = !showCredits
        ? (bounded ? `${reportsWord} scheduled in this window` : `${reportsWord} monitored`)
        : bounded
            ? `${lines.length ? total : 0} credits total over the window`
            : `≈ ${lines.length ? total : 0} credits/month (estimate)`;

    // Credit affordability is warn-but-allow — matching the backend, which never blocks saving on cost and
    // instead pauses each sweep at run time until credits are available. A credit customer who's out of
    // credits must still be able to set monitoring up; the banner just sets expectations, it never blocks Save.
    // (Non-credit clients — showCredits === false — never see any of this.)
    const noBalance = showCredits && balance <= 0;
    // Some credits, but not enough to cover even the first cycle.
    const firstCycleShort = showCredits && balance > 0 && firstCycle > balance;
    // First cycle is covered, but the bounded window's full run will outlast the balance.
    const windowShort = showCredits && balance > 0 && !firstCycleShort && bounded && total > balance;
    const showBalanceNote = runningCount > 0 && (noBalance || firstCycleShort || windowShort);
    const balanceNote = noBalance
        ? `You don't have any credits yet, so scheduled checks won't run until you add some. You can still set monitoring up now — checks begin automatically once credits are added.`
        : firstCycleShort
            ? `The first cycle costs ${firstCycle} credits, more than your balance of ${balance}. Monitoring will be saved, but checks pause until you top up your credits.`
            : `The window total of ${total} credits exceeds your balance of ${balance}. Checks run until the balance is used up, then pause until you top up.`;
    // The no-balance case is informational (you can proceed); a partial shortfall is a softer amber warning.
    const balanceNoteKind = noBalance ? 'is-info' : 'is-warn';

    // Require an end date whenever "No end date (ongoing)" is unchecked — otherwise Save would submit
    // endDate: null and silently create an ongoing schedule the user didn't intend.
    const endDateRequired = !ongoing && !endISO;
    // Affordability never blocks Save (see the warn-but-allow note above) — a no-credit customer must be able
    // to configure monitoring and have it start once they add credits.
    const primaryDisabled = busy || runningCount === 0 || endBeforeStart || endDateRequired;
    const statusEnabled = runningCount > 0;

    const submit = () => {
        if (primaryDisabled) return;
        const selectedReports = reports
            .filter(r => sel[r.key] && r.available)
            .map(r => ({ report: r, cadence: sel[r.key] }));
        onPrimary({
            selectedReports,
            startDate: startISO ? dayjs(startISO).toISOString() : null,
            // End boundary is inclusive of the selected calendar day (the cost estimate uses endOf('day')
            // via windowEnd), so persist it the same way — otherwise a same-day check the estimate counted
            // would fall outside the saved window (midnight) and never run.
            endDate: ongoing || !endISO ? null : dayjs(endISO).endOf('day').toISOString(),
        });
    };

    return (
        <div className='monSetup'>
            {/* Header */}
            <div className='monSetup__head'>
                <div>
                    <div className='monSetup__title'>
                        {bulk ? `Set up monitoring · ${businessCount} business${businessCount === 1 ? '' : 'es'}` : 'Select reports to be monitored'}
                    </div>
                </div>
                {bulk ? (
                    <div className='monSetup__biz'>
                        <span className='monSetup__bizCount'>{businessCount} selected</span>
                        {businesses.slice(0, 3).map((b, i) => <span key={`${b}-${i}`} className='monSetup__chip'>{b}</span>)}
                        {businesses.length > 3 && <span className='monSetup__bizMore'>+{businesses.length - 3}</span>}
                    </div>
                ) : (
                    <span className={`monSetup__status ${statusEnabled ? 'is-on' : ''}`}>
                        <span className='monSetup__statusDot' />{statusEnabled ? 'Enabled' : 'Disabled'}
                    </span>
                )}
            </div>

            <div className='monSetup__body'>
                <div className='monSetup__main'>
                    {/* Reports */}
                    <section className='monSetup__section'>
                        <div className='monSetup__sectionHead'>
                            <span className='monSetup__label'>Reports to monitor</span>
                            <span className='monSetup__labelMuted'>Cadence</span>
                        </div>
                        {reports.map(r => {
                            const checked = !!sel[r.key];
                            return (
                                <div className='monSetup__row' key={r.key}>
                                    <input
                                        type='checkbox'
                                        className='monSetup__check'
                                        checked={checked}
                                        disabled={!r.available}
                                        onChange={() => toggle(r)}
                                        aria-label={r.label}
                                    />
                                    <div className='monSetup__rowText' onClick={() => toggle(r)}>
                                        <span className={`monSetup__rowName ${r.available ? '' : 'is-disabled'}`}>{r.label}</span>
                                        {!r.available && <span className='monSetup__rowReason'>{r.reason}</span>}
                                    </div>
                                    <CadenceSelect
                                        value={sel[r.key]}
                                        disabled={!checked}
                                        allowSubDaily={r.reportType === REPORT_TYPE.FMCSA}
                                        onChange={(v) => setCadence(r, v)}
                                    />
                                </div>
                            );
                        })}
                        <div className='monSetup__hint'>Only reports a business actually has available are enabled and billed.</div>
                    </section>

                    {/* Window */}
                    <section className='monSetup__section'>
                        <span className='monSetup__label'>Monitoring window</span>
                        <div className='monSetup__windowRow'>
                            <label className='monSetup__field'>
                                <span>Start date</span>
                                <input
                                    type='date'
                                    min={todayISO}
                                    value={startISO}
                                    onChange={e => { const v = e.target.value; if (v && v >= todayISO) setStartISO(v); }}
                                />
                            </label>
                            <label className='monSetup__field'>
                                <span>End date</span>
                                <input
                                    type='date'
                                    min={startISO}
                                    value={ongoing ? '' : endISO}
                                    onChange={e => {
                                        const v = e.target.value;
                                        if (v) { setEndISO(v < startISO ? startISO : v); setOngoing(false); }
                                        else { setEndISO(''); setOngoing(true); }
                                    }}
                                />
                            </label>
                        </div>
                        <label className='monSetup__ongoing'>
                            <input
                                type='checkbox'
                                checked={ongoing}
                                onChange={e => { setOngoing(e.target.checked); if (e.target.checked) setEndISO(''); }}
                            />
                            No end date (ongoing)
                        </label>
                        {endBeforeStart && <div className='monSetup__warn'>End date must be on or after the start date.</div>}
                        {endDateRequired && <div className='monSetup__warn'>Enter an end date, or choose “No end date (ongoing)”.</div>}
                    </section>
                </div>

                {/* Cost (credit clients) / Schedule summary (non-credit clients) */}
                <aside className='monSetup__cost'>
                    <div className='monSetup__costHead'>{showCredits ? 'Cost per check' : 'Schedule'}</div>
                    <div className='monSetup__costBody'>
                        {lines.length === 0 && (
                            <div className='monSetup__costEmpty'>
                                {showCredits
                                    ? 'No reports selected. Check at least one report to see its cost.'
                                    : 'No reports selected. Check at least one report to schedule it.'}
                            </div>
                        )}
                        {lines.map(ln => {
                            const zeroReason = windowEnded
                                ? 'this window has already ended'
                                : `window too short to fit a ${cadenceLabel(ln.cadence).toLowerCase()} check`;
                            return (
                                <div className='monSetup__costLine' key={ln.key}>
                                    <div className='monSetup__costTop'>
                                        <span className='monSetup__costName'>
                                            <span className='monSetup__chip'>{ln.label}</span>
                                            <span className='monSetup__costCad'>· {cadenceLabel(ln.cadence)}</span>
                                        </span>
                                        {(ln.zero || showCredits) && (
                                            <span className='monSetup__costCredits'>
                                                {ln.zero ? "Won't run in this window" : ln.creditsText}
                                            </span>
                                        )}
                                    </div>
                                    <div className='monSetup__costSub'>
                                        {ln.zero
                                            ? (showCredits ? `0 credits · ${zeroReason}` : `Won't run — ${zeroReason}`)
                                            : ln.sub}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    {wont.length > 0 && (
                        <div className='monSetup__banner is-warn' role='alert'>
                            {windowEnded
                                ? `This monitoring window has already ended, so ${wont.join(', ')} won't run. Pick an end date in the future, or choose "No end date (ongoing)".`
                                : `The window is too short to fit a check for ${wont.join(', ')}. Extend the end date, or choose "No end date (ongoing)".`}
                        </div>
                    )}
                    {showBalanceNote && (
                        <div className={`monSetup__banner ${balanceNoteKind}`} role={noBalance ? 'status' : 'alert'}>
                            {balanceNote}
                        </div>
                    )}
                    <div className='monSetup__costFoot'>
                        <span className='monSetup__costTotal'>{totalText}</span>
                        {showCredits && (
                            <span className='monSetup__costBalance'>
                                · {balance > 0 ? `balance ${balance} credits` : 'no credits available'}
                            </span>
                        )}
                    </div>
                </aside>
            </div>

            {/* Footer */}
            <div className='monSetup__foot'>
                {onCancel && <button type='button' className='monSetup__btn' onClick={onCancel}>Cancel</button>}
                <button
                    type='button'
                    className='monSetup__btn is-primary'
                    disabled={primaryDisabled}
                    onClick={submit}
                >
                    {busy ? 'Saving…' : (bulk ? `Apply to ${businessCount}` : 'Save')}
                </button>
            </div>
        </div>
    );
};

export default MonitoringSetup;
